import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { wsUrl } from "@/api/client";
import type { ChatClientFrame, ChatServerFrame } from "@/api/types";
import {
  chatMessageReceived,
  chatRoomHeld,
  chatRoomJoined,
  chatRoomLeft,
  chatRoomRefused,
  chatRoomReleased,
  chatRoomViewingStarted,
  chatRoomViewingStopped,
  chatSocketClosed,
  chatSocketOpened,
  chatTicketClaimed,
  chatTicketClosed,
  chatTicketIncoming,
  chatTicketStatus,
  chatTicketRemoved,
  chatTicketUnclaimed,
  fetchChatHistory,
  markChatRoomRead,
} from "@/state/slices/chatSlice";
import { pushToast } from "@/state/slices/notificationsSlice";
import type { AppDispatch, RootState } from "@/state/store";

/** Imperative chat actions for components (via `useChat()`). */
export interface ChatSocketApi {
  /** `holder` (a stable feature name, e.g. "public-room") starts holding
   *  the room. Holds live in chatSlice, so they outlast the component —
   *  don't release on unmount, only on the user's explicit leave. The socket
   *  joins on the first holder (at once, or as soon as it opens) and leaves
   *  on the last. Idempotent per holder. */
  joinRoom: (roomId: string, holder: string) => void;
  leaveRoom: (roomId: string, holder: string) => void;
  /** Send over the socket. Returns false (nothing sent) while it isn't open —
   *  messages are never queued, so the caller keeps the draft. */
  sendMessage: (msg: {
    roomId: string;
    content: string;
    messageType?: "text" | "image";
    originalFilename?: string;
  }) => boolean;
}

export const ChatSocketContext = createContext<ChatSocketApi | null>(null);

/** Chat actions for any component under App's `ChatSocketContext.Provider`. */
export function useChat(): ChatSocketApi {
  const api = useContext(ChatSocketContext);
  if (!api) throw new Error("useChat() used outside ChatSocketContext.Provider");
  return api;
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

/** Delay before reconnect attempt `attempt` (0-based): exponential from 1s,
 *  capped at 30s, ±20% jitter so a server restart isn't met by every client
 *  reconnecting in lockstep. */
export function reconnectDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
  return Math.round(base * (0.8 + 0.4 * random()));
}

const ERROR_TITLES: Record<string, string> = {
  rate_limited: "Slow down — too many messages",
  unauthorized: "Chat: not allowed",
  bad_request: "Chat: message rejected",
};

/**
 * The chat WebSocket (`/api/chat/ws`) — ONE connection for the whole app,
 * opened at App's root once the session is confirmed and kept across
 * navigation (supporters must get `ticket_incoming` wherever they are).
 * Every signed-in user gets it: the community room and their own support
 * ticket aren't supporter-only.
 *
 * Frames are flat `{type, ...}` JSON; each server type maps onto a chatSlice
 * reducer, and `error` frames become toasts.
 *
 * Reconnect (the RFC's contract — a WebSocket has no EventSource-style
 * auto-reconnect): on close, retry with exponential backoff; on each open,
 * re-send `join_room` for every room with a holder (a fresh socket is in
 * no rooms). `chatSocketOpened` bumps `connectionEpoch`, which is the signal for
 * components to re-fetch their room's history over REST before trusting new
 * `new_message` frames — nothing sent while disconnected is replayed.
 *
 * Returns the imperative API; App provides it through `ChatSocketContext`.
 */
export function useChatSocket(): ChatSocketApi {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  // Signed in AND the server's chat flag is on: with chat off there is no
  // socket at all (the handshake would be refused, then retried forever).
  const active = useAppSelector((s) => s.me.status === "succeeded" && s.me.chatEnabled);

  const socketRef = useRef<WebSocket | null>(null);
  // Joins sent but not yet confirmed, to tell a refused join from a refused send.
  const pendingJoinRef = useRef(new Set<string>());

  const sendFrame = useCallback((frame: ChatClientFrame): boolean => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(frame));
    return true;
  }, []);

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let attempt = 0;
    let retryTimer: number | undefined;

    const connect = () => {
      const ws = new WebSocket(wsUrl("/api/chat/ws"));
      socketRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        dispatch(chatSocketOpened());
        pendingJoinRef.current.clear();
        for (const roomId of Object.keys(store.getState().chat.roomHolders)) {
          pendingJoinRef.current.add(roomId);
          ws.send(JSON.stringify({ type: "join_room", room_id: roomId }));
        }
      };

      ws.onmessage = (e: MessageEvent) => {
        if (typeof e.data !== "string") return;
        let frame: ChatServerFrame;
        try {
          frame = JSON.parse(e.data);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("chat socket: failed to parse frame", err);
          return;
        }
        handleFrame(frame, dispatch, pendingJoinRef.current);
      };

      // `error` is always followed by `close`, which does the work.
      ws.onclose = () => {
        if (socketRef.current === ws) socketRef.current = null;
        dispatch(chatSocketClosed());
        if (stopped) return;
        retryTimer = window.setTimeout(connect, reconnectDelayMs(attempt));
        attempt += 1;
      };
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      const ws = socketRef.current;
      socketRef.current = null;
      ws?.close();
      dispatch(chatSocketClosed());
    };
  }, [dispatch, active, store]);

  const joinRoom = useCallback(
    (roomId: string, holder: string) => {
      const holders = store.getState().chat.roomHolders[roomId] ?? [];
      if (holders.includes(holder)) return;
      dispatch(chatRoomHeld({ roomId, holder }));
      if (holders.length === 0) {
        pendingJoinRef.current.add(roomId);
        sendFrame({ type: "join_room", room_id: roomId });
      }
    },
    [dispatch, sendFrame, store],
  );

  const leaveRoom = useCallback(
    (roomId: string, holder: string) => {
      const holders = store.getState().chat.roomHolders[roomId] ?? [];
      if (!holders.includes(holder)) return;
      dispatch(chatRoomReleased({ roomId, holder }));
      if (holders.length > 1) return;
      pendingJoinRef.current.delete(roomId);
      sendFrame({ type: "leave_room", room_id: roomId });
      dispatch(chatRoomLeft(roomId));
    },
    [dispatch, sendFrame, store],
  );

  const sendMessage = useCallback<ChatSocketApi["sendMessage"]>(
    ({ roomId, content, messageType = "text", originalFilename }) =>
      sendFrame({
        type: "send_message",
        room_id: roomId,
        content,
        message_type: messageType,
        ...(originalFilename ? { original_filename: originalFilename } : {}),
      }),
    [sendFrame],
  );

  return useMemo(
    () => ({ joinRoom, leaveRoom, sendMessage }),
    [joinRoom, leaveRoom, sendMessage],
  );
}

function handleFrame(
  frame: ChatServerFrame,
  dispatch: AppDispatch,
  pendingJoins: Set<string>,
): void {
  if (!frame || typeof frame !== "object" || typeof frame.type !== "string") {
    // eslint-disable-next-line no-console
    console.warn("chat socket: frame without a type", frame);
    return;
  }
  switch (frame.type) {
    case "joined":
      pendingJoins.delete(frame.room_id);
      dispatch(chatRoomJoined(frame.room_id));
      return;
    case "new_message": {
      const { type: _type, ...message } = frame;
      dispatch(chatMessageReceived(message));
      return;
    }
    case "ticket_incoming": {
      const { type: _type, ...payload } = frame;
      dispatch(chatTicketIncoming(payload));
      return;
    }
    case "ticket_claimed": {
      const { type: _type, ...payload } = frame;
      dispatch(chatTicketClaimed(payload));
      return;
    }
    case "ticket_closed": {
      const { type: _type, ...payload } = frame;
      dispatch(chatTicketClosed(payload));
      return;
    }
    case "ticket_unclaimed":
      dispatch(chatTicketUnclaimed({ room_id: frame.room_id }));
      return;
    case "ticket_removed":
      dispatch(chatTicketRemoved({ room_id: frame.room_id }));
      return;
    case "ticket_status": {
      const { type: _type, ...payload } = frame;
      dispatch(chatTicketStatus(payload));
      return;
    }
    case "error": {
      const roomId = frame.context?.room_id;
      // A refused join: stop re-requesting it on every reconnect.
      if (frame.code === "unauthorized" && roomId && pendingJoins.has(roomId)) {
        pendingJoins.delete(roomId);
        dispatch(chatRoomRefused(roomId));
      }
      dispatch(
        pushToast({
          kind: frame.code === "rate_limited" ? "warning" : "error",
          title: ERROR_TITLES[frame.code] ?? "Chat error",
          message: frame.message,
        }),
      );
      return;
    }
    default:
      // eslint-disable-next-line no-console
      console.warn("chat socket: unknown frame type", (frame as { type: string }).type);
  }
}

/**
 * Whether `holder` holds `roomId`, and — while it does — keeps the room's
 * cached history trustworthy: fetches the latest page when the room has
 * never been loaded, or when the socket has reconnected since it was
 * (`syncedEpoch` ≠ `connectionEpoch`; the socket replays nothing). After
 * a failed fetch it waits for an explicit retry rather than looping. A
 * component remounting onto a room it still holds therefore renders the
 * cache straight away and fetches nothing, unless there's a possible gap.
 *
 * Joining and leaving stay explicit, via `useChat().joinRoom/leaveRoom`
 * with the same `holder`.
 */
export function useHeldRoom(roomId: string | null, holder: string): { held: boolean } {
  const dispatch = useAppDispatch();
  const held = useAppSelector((s) =>
    roomId ? (s.chat.roomHolders[roomId] ?? []).includes(holder) : false,
  );
  const needsSync = useAppSelector((s) => {
    if (!roomId || !held) return false;
    const room = s.chat.rooms[roomId];
    if (!room) return true;
    // Not while a fetch is in flight, and not after one failed: retrying on
    // every failed→loading→failed flip would hammer the server. A failed
    // room waits for the component's explicit retry.
    if (room.status === "loading" || room.status === "failed") return false;
    return room.syncedEpoch !== s.chat.connectionEpoch;
  });

  useEffect(() => {
    if (needsSync && roomId) void dispatch(fetchChatHistory({ roomId }));
  }, [needsSync, roomId, dispatch]);

  return { held };
}

/** Debounce for re-marking read while a thread is on screen: a burst of
 *  replies becomes one request. */
export const VIEWING_REMARK_MS = 1000;

/**
 * The "I'm viewing this room" signal (RFC read-marking note), for a
 * component that is actually displaying `roomId`'s thread — pass null when
 * it isn't. Marks the room read every time it starts being displayed,
 * regardless of whether `useHeldRoom` needed to fetch anything (a fresh
 * cache still has to be marked read), and again after new messages from
 * others arrive while it stays displayed — live delivery marks nothing read.
 *
 * The display mark waits until the cache is synced with the current
 * connection (`useHeldRoom`'s latest page has landed): marking a stale cache
 * read would clear the badge for messages the thread isn't showing.
 */
export function useRoomViewing(roomId: string | null): void {
  const dispatch = useAppDispatch();
  const synced = useAppSelector((s) => {
    const room = roomId ? s.chat.rooms[roomId] : undefined;
    return !!room && room.status === "succeeded" && room.syncedEpoch === s.chat.connectionEpoch;
  });
  // undefined until the room's history has loaded once: the first page
  // landing isn't "new messages arriving while viewing".
  const latestFromOthers = useAppSelector((s): number | null | undefined => {
    if (!roomId) return undefined;
    const room = s.chat.rooms[roomId];
    if (!room || room.syncedEpoch === null) return undefined;
    for (let i = room.messages.length - 1; i >= 0; i -= 1) {
      if (room.messages[i].sender_id !== s.me.sub) return room.messages[i].id;
    }
    return null;
  });
  const seenRef = useRef<{ roomId: string; id: number | null } | null>(null);

  // Every display, unconditionally, recorded in chatSlice (`viewingRooms`)
  // so reducers can tell "on screen" from merely joined.
  useEffect(() => {
    if (!roomId) return;
    dispatch(chatRoomViewingStarted(roomId));
    return () => {
      dispatch(chatRoomViewingStopped(roomId));
    };
  }, [roomId, dispatch]);

  // The display mark: once per display, as soon as the shown cache is
  // current — again after a reconnect's re-fetch.
  useEffect(() => {
    if (roomId && synced) void dispatch(markChatRoomRead(roomId));
  }, [roomId, synced, dispatch]);

  // New messages from others while still displayed.
  useEffect(() => {
    if (!roomId) {
      seenRef.current = null;
      return;
    }
    if (latestFromOthers === undefined) return; // not loaded yet
    const seen = seenRef.current;
    seenRef.current = { roomId, id: latestFromOthers };
    if (!seen || seen.roomId !== roomId) return; // baseline: covered by the display mark
    if (latestFromOthers === null || (seen.id !== null && latestFromOthers <= seen.id)) return;
    const timer = window.setTimeout(() => void dispatch(markChatRoomRead(roomId)), VIEWING_REMARK_MS);
    return () => window.clearTimeout(timer);
  }, [roomId, latestFromOthers, dispatch]);
}
